import React, { useState } from 'react';
import EmailDashboard from './EmailDashboard';
import EmailCampaigns from './EmailCampaigns';
import ModernEmailCampaigns from './ModernEmailCampaigns';
import EmailMailboxes from './EmailMailboxes';
import EmailTemplates from './EmailTemplates';
import EmailContacts from './EmailContacts';
import UnifiedInbox from '../components/email/UnifiedInbox';
import EmailAnalyticsDashboard from '../components/email/analytics/EmailAnalyticsDashboard';
import EmailDeliverabilityHub from '../components/email/analytics/EmailDeliverabilityHub';
import EmailABTestList from '../components/email/analytics/EmailABTestList';
import CampaignComparison from '../components/email/analytics/CampaignComparison';
import LeadIntelligenceDashboard from '../components/email/analytics/LeadIntelligenceDashboard';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Mail,
  Inbox,
  FileText,
  Users,
  MailOpen,
  BarChart3,
  FlaskConical,
  GitCompare,
  Target,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Sparkles
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  group?: string;
}

const ModernEmailInterface: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [groupCollapsed, setGroupCollapsed] = useState<{ main: boolean; manage: boolean; insights: boolean}>(
    { main: false, manage: false, insights: false }
  );
  
  const getInitialTab = (): string => {
    const path = location.pathname;
    if (path.includes('/campaigns')) return 'campaigns';
    if (path.includes('/mailboxes')) return 'mailboxes';
    if (path.includes('/templates')) return 'templates';
    if (path.includes('/contacts')) return 'contacts';
    if (path.includes('/unified-inbox')) return 'unified-inbox';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/ab-tests')) return 'ab-tests';
    if (path.includes('/campaign-comparison')) return 'campaign-comparison';
    if (path.includes('/lead-intelligence')) return 'lead-intelligence';
    return 'dashboard';
  };

  const toggleGroup = (group: 'main' | 'manage' | 'insights') => {
    setGroupCollapsed(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab());

  const navItems: NavItem[] = [
    { id: 'dashboard', label: 'Overview', icon: LayoutDashboard, group: 'main' },
    { id: 'campaigns', label: 'Campaigns', icon: Mail, group: 'main' },
    { id: 'unified-inbox', label: 'Inbox', icon: Inbox, badge: 5, group: 'main' },
    { id: 'lead-intelligence', label: 'Leads', icon: Target, badge: 12, group: 'main' },
    { id: 'contacts', label: 'Contacts', icon: Users, group: 'manage' },
    { id: 'mailboxes', label: 'Mailboxes', icon: MailOpen, group: 'manage' },
    { id: 'templates', label: 'Templates', icon: FileText, group: 'manage' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, group: 'insights' },
    { id: 'deliverability', label: 'Deliverability', icon: MailOpen, group: 'insights' },
    { id: 'campaign-comparison', label: 'Compare', icon: GitCompare, group: 'insights' },
    { id: 'ab-tests', label: 'A/B Tests', icon: FlaskConical, group: 'insights' },
  ];

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    let path = '/email';
    if (tab !== 'dashboard') {
      path += `/${tab}`;
    }
    navigate(path, { replace: true });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'campaigns':
        return <ModernEmailCampaigns />;
      case 'mailboxes':
        return <EmailMailboxes />;
      case 'templates':
        return <EmailTemplates />;
      case 'contacts':
        return <EmailContacts />;
      case 'unified-inbox':
        return <UnifiedInbox />;
      case 'analytics':
        return <EmailAnalyticsDashboard />;
      case 'deliverability':
        return <EmailDeliverabilityHub />;
      case 'ab-tests':
        return <EmailABTestList />;
      case 'campaign-comparison':
        return <CampaignComparison />;
      case 'lead-intelligence':
        return <LeadIntelligenceDashboard />;
      default:
        return <EmailDashboard />;
    }
  };

  const groupedNav = {
    main: navItems.filter(item => item.group === 'main'),
    manage: navItems.filter(item => item.group === 'manage'),
    insights: navItems.filter(item => item.group === 'insights'),
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarCollapsed ? 'w-16' : 'w-64'
        } bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out`}
      >
        {/* Logo/Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-gray-900">Cold Email</span>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {/* Main Section */}
          <div className="px-3 mb-6">
            {!sidebarCollapsed && (
              <button
                type="button"
                onClick={() => toggleGroup('main')}
                className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2"
              >
                <span>Main</span>
                <span className="ml-2">
                  {groupCollapsed.main ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronUp className="w-3 h-3" />
                  )}
                </span>
              </button>
            )}
            {!groupCollapsed.main && groupedNav.main.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-all ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-700' : 'text-gray-500'}`} />
                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
                      {item.badge && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                  {sidebarCollapsed && item.badge && (
                    <span className="absolute right-1 top-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Manage Section */}
          <div className="px-3 mb-6">
            {!sidebarCollapsed && (
              <button
                type="button"
                onClick={() => toggleGroup('manage')}
                className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2"
              >
                <span>Manage</span>
                <span className="ml-2">
                  {groupCollapsed.manage ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronUp className="w-3 h-3" />
                  )}
                </span>
              </button>
            )}
            {!groupCollapsed.manage && groupedNav.manage.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-all ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-700' : 'text-gray-500'}`} />
                  {!sidebarCollapsed && (
                    <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Insights Section */}
          <div className="px-3">
            {!sidebarCollapsed && (
              <button
                type="button"
                onClick={() => toggleGroup('insights')}
                className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2"
              >
                <span>Insights</span>
                <span className="ml-2">
                  {groupCollapsed.insights ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronUp className="w-3 h-3" />
                  )}
                </span>
              </button>
            )}
            {!groupCollapsed.insights && groupedNav.insights.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-all ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-700' : 'text-gray-500'}`} />
                  {!sidebarCollapsed && (
                    <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Footer - User info or help */}
        {!sidebarCollapsed && (
          <div className="p-4 border-t border-gray-200">
            <div className="text-xs text-gray-600">
              <p className="font-medium text-gray-900 mb-1">Need Help?</p>
              <button
                onClick={() => navigate('/cold-email-docs')}
                className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                View Documentation →
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-gray-200 px-6 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {navItems.find(item => item.id === activeTab)?.label || 'Overview'}
            </h1>
            <p className="text-sm text-gray-500">
              {activeTab === 'dashboard' && 'Monitor your cold email performance'}
              {activeTab === 'campaigns' && 'Create and manage email campaigns'}
              {activeTab === 'unified-inbox' && 'View all campaign replies in one place'}
              {activeTab === 'lead-intelligence' && 'AI-powered lead scoring and prioritization'}
              {activeTab === 'contacts' && 'Manage your contact lists'}
              {activeTab === 'mailboxes' && 'Configure email sending accounts'}
              {activeTab === 'templates' && 'Create reusable email templates'}
              {activeTab === 'analytics' && 'Track campaign performance metrics'}
              {activeTab === 'deliverability' && 'Monitor mailbox, domain, and warmup health so beginners avoid spam folders'}
              {activeTab === 'campaign-comparison' && 'Compare campaign performance'}
              {activeTab === 'ab-tests' && 'Run A/B tests on your campaigns'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
              Help
            </button>
            <button
              onClick={() => handleTabChange('campaigns')}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              New Campaign
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </main>
    </div>
  );
};

export default ModernEmailInterface;
